// The ONE lift-device decision, so decoders and lifter cannot disagree about which GPU owns a buffer.

import { wantsProfiling } from "./perfFlags";
import { installWebGpuLimitPatch } from "../../lib/webgpuLimits.js";
import { ogsGpuFlags, type OgsGpuFlags } from "./ogsGpuFlags";

// WebGPU's DEFAULT maxStorageBuffersPerShaderStage, which every adapter grants — the lift's
// heaviest pass binds exactly 8, so no device is refused the GPU lift over a limit.
const REQUIRED_STORAGE_BUFFERS = 8;
// Headroom only; clamped to the adapter maximum, so asking never costs a rejected create.
const REQUESTED_STORAGE_BUFFERS = 10;

/** timestamp-query under ?perf=1 only, and only when the adapter has it — else create rejects. */
function profilingFeatures(adapter: GPUAdapter): GPUFeatureName[] {
  return wantsProfiling() && adapter.features?.has?.("timestamp-query")
    ? ["timestamp-query"]
    : [];
}

let devicePromise: Promise<GPUDevice | null> | null = null;

let sharedSetupDone = false;
let sharedSetupOk = false;

/** Make ORT build a device the lift can share. MUST run before runner.init(); never throws. */
export async function setupSharedOrtAdapter(ort: any): Promise<boolean> {
  if (sharedSetupDone) return sharedSetupOk;
  sharedSetupDone = true;
  try {
    const webgpu = (navigator as unknown as { gpu?: GPU }).gpu;
    if (!webgpu || !ort?.env?.webgpu) return false;
    // high-performance picks the discrete GPU; the default runs all inference on integrated.
    const adapter = await webgpu.requestAdapter({ powerPreference: "high-performance" });
    if (!adapter) return false;
    const adapterMaximum = adapter.limits?.maxStorageBuffersPerShaderStage ?? 8;
    if (Math.min(REQUESTED_STORAGE_BUFFERS, adapterMaximum) < REQUIRED_STORAGE_BUFFERS) return false;
    // Re-assert of the boot patch, for a caller that arrives without main.jsx's import.
    installWebGpuLimitPatch();
    try { ort.env.webgpu.powerPreference = "high-performance"; } catch { /* read-only on some builds */ }
    sharedSetupOk = true;
    return true;
  } catch (error) {
    console.warn("[texavatars] shared ORT adapter setup failed — lift uses its own device/CPU", error);
    return false;
  }
}

/** The device ORT actually built, if it satisfies the lift's storage-buffer need. */
export function getSharedOrtDevice(ort: any): GPUDevice | null {
  const ortDevice = ort?.env?.webgpu?.device as GPUDevice | undefined;
  if (!ortDevice) return null;
  const granted = ortDevice.limits?.maxStorageBuffersPerShaderStage ?? 0;
  return granted >= REQUIRED_STORAGE_BUFFERS ? ortDevice : null;
}

export type OgsLiftMode = "shared" | "own" | "cpu";

export interface OgsLiftPrepared {
  /** Emit gpu-buffer outputs + build lifters/pluckers on ORT's device. */
  readonly shared: boolean;
  readonly flags: OgsGpuFlags;
}

export interface OgsLiftDevice {
  readonly device: GPUDevice | null;
  readonly mode: OgsLiftMode;
  /** The sessions' gpu-buffer hints must be DEMOTED to CPU downloads. */
  readonly demoted: boolean;
}

/** Step 1: ask ORT for a shareable device. Call BEFORE creating any session. */
export async function prepareOgsLiftDevice(
  ort: any,
  providers: string[],
  flags: OgsGpuFlags = ogsGpuFlags(),
): Promise<OgsLiftPrepared> {
  const wantShared = flags.gpuBufLift && providers[0] === "webgpu";
  const shared = wantShared ? await setupSharedOrtAdapter(ort) : false;
  return { shared, flags };
}

/** Step 3: the device to build lifters on, AFTER the sessions exist. shared → own → CPU. */
export async function resolveOgsLiftDevice(
  ort: any,
  prepared: OgsLiftPrepared,
): Promise<OgsLiftDevice> {
  const { flags } = prepared;
  if (prepared.shared) {
    const shared = getSharedOrtDevice(ort);
    if (shared) return { device: shared, mode: "shared", demoted: false };
    // Demote NOW, or the lifter gets cross-device buffers the sessions still hint at.
    console.warn(
      "[texavatars] ORT's actual WebGPU device fails the lift limits (adapter mismatch?) — demoting: outputs read back to CPU, lift uses its own device/CPU",
    );
    const ownDevice = flags.gpuLift ? await getTexavatarsGpuDevice() : null;
    return { device: ownDevice, mode: ownDevice ? "own" : "cpu", demoted: true };
  }
  if (flags.gpuLift) {
    const ownDevice = await getTexavatarsGpuDevice();
    if (ownDevice) return { device: ownDevice, mode: "own", demoted: false };
  }
  console.warn(
    `[ogs] lift: CPU (GaussianLifter) — no WebGPU adapter (or ?gpulift=0 / a device granting < ${REQUIRED_STORAGE_BUFFERS} storage buffers). The branch reports this as a downgrade.`,
  );
  return { device: null, mode: "cpu", demoted: false };
}

export function getTexavatarsGpuDevice(): Promise<GPUDevice | null> {
  return getOgsComputeDevice(REQUIRED_STORAGE_BUFFERS, "GpuLifter");
}

/** One cached device, refused per CALLER: lift_pass1 binds 8, the eye's polar lift binds 5. */
export function getOgsComputeDevice(
  minStorageBuffers: number,
  caller = "lift",
): Promise<GPUDevice | null> {
  if (!devicePromise) {
    devicePromise = (async () => {
      try {
        const webgpu = (navigator as unknown as { gpu?: GPU }).gpu;
        if (!webgpu) return null;
        const adapter = await webgpu.requestAdapter({ powerPreference: "high-performance" });
        if (!adapter) return null;
        // Clamp the request to the adapter maximum so requestDevice never rejects.
        const adapterMaximum = adapter.limits?.maxStorageBuffersPerShaderStage ?? 8;
        return await adapter.requestDevice({
          requiredLimits: {
            maxStorageBuffersPerShaderStage: Math.min(REQUESTED_STORAGE_BUFFERS, adapterMaximum),
          },
          requiredFeatures: profilingFeatures(adapter),
        });
      } catch (error) {
        console.warn("[texavatars] WebGPU compute device unavailable", error);
        return null;
      }
    })();
  }
  return devicePromise.then((device) => {
    if (!device) return null;
    if (grants(device, minStorageBuffers)) return device;
    console.warn(
      `[texavatars] ${caller} needs maxStorageBuffersPerShaderStage>=${minStorageBuffers}, this device grants ${device.limits?.maxStorageBuffersPerShaderStage}`,
    );
    return null;
  });
}

function grants(device: GPUDevice, minStorageBuffers: number): boolean {
  return (device.limits?.maxStorageBuffersPerShaderStage ?? 8) >= minStorageBuffers;
}
